import { Component } from "react"
import PropTypes from "prop-types"

// components
import { createOrderByFilterGroup, genericFilterGroup } from "shared/search/containers/FilterGroup/FilterGroups"
import {
    AmendmentMentionFilter,
    BillMentionFilter,
    CommitteeMentionFilter,
    createListFilter,
    createNotInListFilter,
    ManuallyChangedInListFilter,
    DateListNoFutureFilter,
    RegionCountryFilter,
    RegionCountryEUFilter,
    OfficialMentionFilter,
    PressMentionFilter,
    RegionFilter,
    VoteMentionFilter,
    LegislativeSessionFilter,
    StanceFilter,
    StanceLastUserFilter,
    PopulationStratumFilter,
    ReviewedByMeFilter,
    ReviewedByOrgFilter,
    ReviewedByTeamFilter,
    ReviewedByUserFilter,
    NotReviewedByUserFilter,
    // for local
    DateListFilter,
    DatePickerEndFilter,
    DatePickerStartFilter,
} from "shared/search/containers/Filter/Filters"
import { inIssueFilter, notInIssueFilter } from "shared/search/containers/Filter/FilterCreators"
import {
    DocumentCampaignSourceFilter,
    DocumentChamberFilter,
    DocumentCommitteProfileFilter,
    DocumentCountyFilter,
    DocumentCurrentSourceFilter,
    DocumentDatePickerStartNoFutureFilter,
    DocumentDatePickerEndNoFutureFilter,
    DocumentIsRetweetFilter,
    DocumentLanguageFilter,
    DocumentMediaOutletSourceFilter,
    DocumentMunicipalityFilter,
    DocumentPartyFilter,
    DocumentPartyInternationalFilter,
    DocumentPersonSourceFilter,
    DocumentPressContactSourceFilter,
    DocumentPublicOrganizationSourceFilter,
    DocumentSchoolDistrictFilter,
    DocumentSourceCaucusFilter,
    DocumentSourceCommitteeFilter,
    DocumentSourceTypeFilter,
    DocumentSupporterSourceFilter,
    DocumentTrackingDashboardFilter,
    DocumentTypeStatementsFilter,
    DocumentTypeSocialFilter,
    DocumentTypeFilter,
    StateBillDocumentTypeFilter,
    DocumentTypeSocialOnlyFilter,
    // invisible
    DocumentHashtagFilter,
    DocumentMentionFilter,
    DocumentRetweetFilter,
    // Organization profile
    DocumentOrganizatonsFilter,
    DocumentMembersOrganizatonsFilter,
    DocumentTypeAxiosArticleFilter,
    DocumentsRepresentingSchoolDistrictEnrollmentMaxFilter,
    DocumentsRepresentingSchoolDistrictEnrollmentMinFilter,
} from "shared/search/containers/Filter/DocumentFilters"

// helpers
import { checkHasRegion, checkIfShouldShowSchoolBoard } from "shared/imports/permissionFunctions"
import { localRegions } from "shared/imports/regionConstants"
import isFeatureEnabled from "shared/featureflags/helperFunctions"

const defaultProps = {
    filters: PropTypes.array,
    uniqueFilterGroupId: PropTypes.string,
    displayLabel: PropTypes.string,
    helpText: PropTypes.string,
}

const { isExclusivelySchoolBoard, canSeeSchoolBoards } = checkIfShouldShowSchoolBoard()
export class DocumentTypeFilterGroup extends Component {
    static defaultProps = {
        filters: Userdata.isOnlyEUMode()
            ? [DocumentTypeSocialFilter, DocumentIsRetweetFilter, DocumentTypeFilter]
            : [
                  ...(isFeatureEnabled("ff_axios_documents_integration") ? [DocumentTypeAxiosArticleFilter] : []),
                  // For School Boards, we need to check if the user doesn't have other local permissions.
                  // This is due the overlapping between both permissions and what we actually need to show in the filter.
                  ...(isExclusivelySchoolBoard
                      ? []
                      : [DocumentTypeStatementsFilter, DocumentTypeSocialFilter, DocumentIsRetweetFilter]),
                  DocumentTypeFilter,
                  ...(isFeatureEnabled("ff_state_bill_document") ? [StateBillDocumentTypeFilter] : []),
              ],
        uniqueFilterGroupId: "documentTypeFilterGroup",
        displayLabel: "Document Type",
        helpText:
            "Filter documents by type, whether statements, " +
            "social media, reports, and any of the other tens of document " +
            "types stored by Quorum.",
    }
    render() {
        return genericFilterGroup(this.props)
    }
}

export class DocumentTypeCommsOnlyFilterGroup extends Component {
    static defaultProps = {
        filters: [DocumentIsRetweetFilter],
        uniqueFilterGroupId: "documentTypeCommsOnlyFilterGroup",
        displayLabel: "Type",
        helpText: "Filter social media by type.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class DocumentTypeSocialOnlyFilterGroup extends Component {
    static defaultProps = {
        filters: [DocumentTypeSocialOnlyFilter, DocumentIsRetweetFilter],
        uniqueFilterGroupId: "documentTypeForPressContactFilterGroup",
        displayLabel: "Document Type",
        helpText: "Filter documents by social media type",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class DocumentSourceFilterGroup extends Component {
    static defaultProps = {
        filters: [
            DocumentSourceTypeFilter,
            DocumentPersonSourceFilter,
            DocumentPressContactSourceFilter,
            DocumentMediaOutletSourceFilter,
            DocumentCampaignSourceFilter,
            DocumentCurrentSourceFilter,
            DocumentChamberFilter,
            DocumentPartyFilter,
            DocumentPartyInternationalFilter,
            DocumentSourceCommitteeFilter,
            DocumentSourceCaucusFilter,
            DocumentLanguageFilter,
            DocumentSupporterSourceFilter,
            DocumentPublicOrganizationSourceFilter,
        ],
        uniqueFilterGroupId: "documentSourceFilterGroup",
        displayLabel: "Source",
        helpText:
            "Filter documents by author, such as a legislator or committee. " +
            "If you'd like to see documents from a group of authors, you can choose " +
            "one of Quorum's lists or use one that you've saved to Projects.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class DocumentDateFilterGroup extends Component {
    static defaultProps = {
        filters: [
            ...(checkHasRegion(localRegions)
                ? [DateListFilter, DatePickerEndFilter, DatePickerStartFilter]
                : [DateListNoFutureFilter, DocumentDatePickerStartNoFutureFilter, DocumentDatePickerEndNoFutureFilter]),
            LegislativeSessionFilter,
        ],
        uniqueFilterGroupId: "documentDateFilterGroup",
        displayLabel: "Date",
        helpText: "Filter for results that took place after a particular date " + "or within a specific date range.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class DocumentMentionFilterGroup extends Component {
    static defaultProps = {
        filters: [
            OfficialMentionFilter,
            BillMentionFilter,
            VoteMentionFilter,
            AmendmentMentionFilter,
            CommitteeMentionFilter,
            PressMentionFilter,
        ],
        uniqueFilterGroupId: "documentMentionFilterGroup",
        displayLabel: "Referencing",
        helpText:
            "Filter documents that reference a specific legislator, " +
            "bill, vote, etc. We use natural language processing to automatically " +
            "form these relationships.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class DocumentMentionCommsOnlyFilterGroup extends Component {
    static defaultProps = {
        filters: [PressMentionFilter],
        uniqueFilterGroupId: "documentMentionCommsOnlyFilterGroup",
        displayLabel: "Referencing",
        helpText:
            "Filter social media that reference a specific press contact, " +
            "etc. We use natural language processing to automatically " +
            "form these relationships.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class DocumentRegionFilterGroup extends Component {
    static defaultProps = {
        // one of these will be filtered out due to regions (US vs EU)
        filters: [
            RegionFilter,
            RegionCountryFilter,
            RegionCountryEUFilter,
            DocumentCountyFilter,
            DocumentMunicipalityFilter,
            ...(canSeeSchoolBoards
                ? [
                      DocumentSchoolDistrictFilter,
                      ...(isFeatureEnabled("ff_school_board_orgs_and_officials")
                          ? [
                                DocumentsRepresentingSchoolDistrictEnrollmentMinFilter,
                                DocumentsRepresentingSchoolDistrictEnrollmentMaxFilter,
                            ]
                          : []),
                  ]
                : []),
            PopulationStratumFilter,
        ],
        uniqueFilterGroupId: "documentRegionFilterGroup",
        displayLabel: "Region",
        helpText: "Filter for documents from a single region or a group of regions.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class DocumentListFilterGroup extends Component {
    static defaultProps = {
        filters: [
            createListFilter(DjangIO.app.models.QuorumDataType.document.value),
            createNotInListFilter(DjangIO.app.models.QuorumDataType.document.value),
            ManuallyChangedInListFilter,
        ],
        uniqueFilterGroupId: "documentListFilterGroup",
        displayLabel: "Lists",
        helpText: "Filter documents by lists of documents that you've created " + "and saved to Projects.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class DocumentStanceFilterGroup extends Component {
    static defaultProps = {
        filters: [StanceFilter, StanceLastUserFilter],
        uniqueFilterGroupId: "documentStanceFilterGroup",
        displayLabel: "Stance",
        helpText: "Filter documents by your organization's stance.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class DocumentInvisibleFilterGroup extends Component {
    static defaultProps = {
        filters: [DocumentHashtagFilter, DocumentRetweetFilter, DocumentMentionFilter],
        uniqueFilterGroupId: "documentInvisibleFilterGroup",
        invisible: true,
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export const DocumentOrganizationProfileFilterGroup = (props) => genericFilterGroup(props)
DocumentOrganizationProfileFilterGroup.propTypes = defaultProps
DocumentOrganizationProfileFilterGroup.defaultProps = {
    filters: [DocumentOrganizatonsFilter],
    uniqueFilterGroupId: "documentOrganizationProfileFilterGroup",
    noHeader: true,
    alwaysOpen: true,
}

export const DocumentMembersOrganizationProfileFilterGroup = (props) => genericFilterGroup(props)
DocumentMembersOrganizationProfileFilterGroup.propTypes = defaultProps
DocumentMembersOrganizationProfileFilterGroup.defaultProps = {
    filters: [DocumentMembersOrganizatonsFilter],
    uniqueFilterGroupId: "documentMembersOrganizationProfileFilterGroup",
    noHeader: true,
    alwaysOpen: true,
}

export const DocumentProjectFilterGroup = (props) => genericFilterGroup(props)
DocumentProjectFilterGroup.propTypes = defaultProps
DocumentProjectFilterGroup.defaultProps = {
    filters: [inIssueFilter, notInIssueFilter],
    uniqueFilterGroupId: "documentProjectFilterGroup",
    displayLabel: "Issues",
    helpText: "Filter results by the issue they are tagged to.",
}

export const DocumentCommitteeProfileFilterGroup = (props) => genericFilterGroup(props)
DocumentCommitteeProfileFilterGroup.propTypes = defaultProps
DocumentCommitteeProfileFilterGroup.defaultProps = {
    filters: [DocumentCommitteProfileFilter],
    uniqueFilterGroupId: "documentCommitteProfileFilterGroup",
    noHeader: true,
    alwaysOpen: true,
}

export const DocumentTrackingDashboardFilterGroup = (props) => genericFilterGroup(props)
DocumentTrackingDashboardFilterGroup.propTypes = defaultProps
DocumentTrackingDashboardFilterGroup.defaultProps = {
    filters: [DocumentTrackingDashboardFilter],
    uniqueFilterGroupId: "documentTrackingDashboardFilter",
    displayLabel: "Tracking Boards",
    helpText: "Filter for results that currently appear on the unreviewed documents tab in your tracking board(s).",
}

export const DocumentOrderByFilterGroup = createOrderByFilterGroup({
    // uniqueFilterId = "documentOrderBy"
    quorumDataType: DjangIO.app.models.QuorumDataType.document.value,
})

export const DocumentReviewFilterGroup = (props) => genericFilterGroup(props)
DocumentReviewFilterGroup.propTypes = defaultProps
DocumentReviewFilterGroup.defaultProps = {
    filters: [
        ReviewedByMeFilter,
        ReviewedByOrgFilter,
        ReviewedByUserFilter,
        ReviewedByTeamFilter,
        NotReviewedByUserFilter,
    ],
    uniqueFilterGroupId: "documentReviewFilterGroup",
    displayLabel: "Review Status",
    helpText: "Filter results by review status of document",
}
